/* Bettr Sauna — Custom Element registry
 * --------------------------------------------------------------------
 * Phase 13: Custom Element wrappers over existing components. No
 * Shadow DOM — merchants must be able to edit component markup from
 * the Shopify Theme Editor, and shadow trees hide children from the
 * section-settings inspector.
 *
 * Every wrapper defaults to `display: contents` (handled in the
 * stylesheet below) so the CE adds zero layout. Existing CSS selectors
 * targeting the inner markup continue to match.
 *
 * What CEs provide over the plain IIFE+section:load pattern:
 *   - A stable registry: customElements.get('bettr-cart-drawer') works
 *     from anywhere, no brittle DOM probing.
 *   - Explicit connectedCallback/disconnectedCallback — Theme Editor
 *     section reloads trigger these automatically, no separate
 *     shopify:section:unload hook.
 *   - attributeChangedCallback for reactive attribute surfaces
 *     (data-selected-variant-id, data-product-handle, etc.).
 *   - Future-ready: declarative Shadow DOM attachment is a one-line
 *     change per class if/when that becomes desirable.
 * -------------------------------------------------------------------- */

(function () {
  if (!('customElements' in window)) return;

  // ------------------------------------------------------------------
  // Base mix-in: lifecycle hooks fire custom events + track mount.
  // ------------------------------------------------------------------
  class BettrBaseElement extends HTMLElement {
    constructor() {
      super();
      this._mounted = false;
    }
    connectedCallback() {
      if (this._mounted) return;
      this._mounted = true;
      if (typeof this.onMount === 'function') this.onMount();
      this.dispatchEvent(new CustomEvent('bettr:mount', { bubbles: false }));
    }
    disconnectedCallback() {
      if (!this._mounted) return;
      this._mounted = false;
      if (typeof this.onUnmount === 'function') this.onUnmount();
      this.dispatchEvent(new CustomEvent('bettr:unmount', { bubbles: false }));
    }
  }

  // ------------------------------------------------------------------
  // <bettr-cart-drawer> — thin wrapper. The cart drawer section file
  // already owns its own IIFE; this just provides a stable querySelector
  // target and a reliable unmount hook for Theme Editor section reloads.
  // ------------------------------------------------------------------
  class BettrCartDrawer extends BettrBaseElement {
    get isOpen() {
      const section = this.querySelector('.cart-drawer-section');
      return !!section && section.getAttribute('aria-hidden') === 'false';
    }
  }

  // ------------------------------------------------------------------
  // <bettr-compare-modal>
  // ------------------------------------------------------------------
  class BettrCompareModal extends BettrBaseElement {
    open() {
      if (typeof window.openCompareModal === 'function') window.openCompareModal();
    }
  }

  // ------------------------------------------------------------------
  // <bettr-addon-modal>
  // ------------------------------------------------------------------
  class BettrAddonModal extends BettrBaseElement {}

  // ------------------------------------------------------------------
  // <bettr-variant-selector> — reactive variant attribute.
  // Consumers watch for `bettr:variant-change`; PDP JS dispatches.
  // ------------------------------------------------------------------
  class BettrVariantSelector extends BettrBaseElement {
    static get observedAttributes() {
      return ['data-selected-variant-id'];
    }
    attributeChangedCallback(name, oldValue, newValue) {
      if (!this._mounted) return;
      if (name === 'data-selected-variant-id' && oldValue !== newValue) {
        this.dispatchEvent(new CustomEvent('bettr:variant-change', {
          detail: { variantId: newValue },
          bubbles: true,
        }));
      }
    }
  }

  // ------------------------------------------------------------------
  // <bettr-lineup-card> — exposes a robust productHandle getter that
  // fixes the Phase 4d fallback fragility:
  //   1. data-product-handle attribute (explicit override, highest priority)
  //   2. first internal /products/<handle> link in the card
  //   3. '' (caller decides)
  //
  // View Transition names are still emitted server-side from Liquid;
  // this getter lets JS consumers (e.g. Phase 21 analytics) resolve the
  // handle cheaply without re-parsing HTML.
  // ------------------------------------------------------------------
  class BettrLineupCard extends BettrBaseElement {
    get productHandle() {
      const explicit = this.getAttribute('data-product-handle');
      if (explicit) return explicit;
      const a = this.querySelector('a[href*="/products/"]');
      if (!a) return '';
      const m = a.getAttribute('href').match(/\/products\/([^/?#]+)/);
      return m ? m[1] : '';
    }
  }

  // <bettr-qty-input> removed 2026-05-23 — the form-associated custom
  // element was scaffolded for a future cart drawer qty refactor but
  // had zero markup consumers. Reintroduce from git history if/when
  // the qty UI rework lands; see commit history for the full class.

  // ------------------------------------------------------------------
  // <bettr-share-button> — Phase 25 Web Share API integration.
  // Usage:
  //   <bettr-share-button data-title="Ritual 2" data-url="/products/ritual-2">
  //     <button>Share</button>
  //   </bettr-share-button>
  //
  // Uses navigator.share when available (mobile + macOS Safari), falls
  // back to clipboard write + a toast. Hides its slotted content when
  // neither is supported so non-capable browsers don't see a dead button.
  // ------------------------------------------------------------------
  class BettrShareButton extends BettrBaseElement {
    onMount() {
      this._hasShare = typeof navigator.share === 'function';
      this._hasClipboard = !!(navigator.clipboard && navigator.clipboard.writeText);
      if (!this._hasShare && !this._hasClipboard) {
        this.style.display = 'none';
        return;
      }
      this._onClick = (e) => this._trigger(e);
      this.addEventListener('click', this._onClick);
    }
    onUnmount() {
      if (this._onClick) this.removeEventListener('click', this._onClick);
    }
    async _trigger(e) {
      const btn = e.target && e.target.closest('button, a');
      if (!btn) return;
      e.preventDefault();
      const url = new URL(this.getAttribute('data-url') || location.pathname, location.origin).toString();
      const title = this.getAttribute('data-title') || document.title;
      const text = this.getAttribute('data-text') || '';
      try {
        if (this._hasShare) {
          await navigator.share({ title, text, url });
          this.dispatchEvent(new CustomEvent('bettr:share', { detail: { method: 'share' }, bubbles: true }));
          return;
        }
        if (this._hasClipboard) {
          await navigator.clipboard.writeText(url);
          this._toast('Link copied');
          this.dispatchEvent(new CustomEvent('bettr:share', { detail: { method: 'clipboard' }, bubbles: true }));
        }
      } catch (err) { /* user cancelled — silent */ }
    }
    _toast(msg) {
      const t = document.createElement('div');
      t.textContent = msg;
      t.setAttribute('role', 'status');
      t.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#0C0F09;color:#FFF;padding:10px 18px;border-radius:999px;font-size:14px;z-index:10001;opacity:0;transition:opacity .2s ease-out;pointer-events:none';
      document.body.appendChild(t);
      requestAnimationFrame(() => { t.style.opacity = '1'; });
      setTimeout(() => {
        t.style.opacity = '0';
        setTimeout(() => t.remove(), 220);
      }, 1600);
    }
  }

  // ------------------------------------------------------------------
  // Register all.
  // ------------------------------------------------------------------
  const registry = [
    ['bettr-cart-drawer', BettrCartDrawer],
    ['bettr-compare-modal', BettrCompareModal],
    ['bettr-addon-modal', BettrAddonModal],
    ['bettr-variant-selector', BettrVariantSelector],
    ['bettr-lineup-card', BettrLineupCard],
    ['bettr-share-button', BettrShareButton],
  ];
  registry.forEach(([name, ctor]) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  });
})();
